import React, { ReactNode } from 'react';
import { AuthProvider, useAuth as useRealAuth } from './AuthContext';
import { MockAuthProvider, useMockAuth } from './MockAuthContext';
import { logEnvironmentInfo } from '@/utils/envTest';

interface AuthWrapperProps {
  children: ReactNode;
}

/**
 * Determine auth mode at build time to avoid conditional hooks
 * This is evaluated once when the module loads
 */
const AUTH_MODE = (() => {
  // Production builds always use real auth
  if (process.env.NODE_ENV === 'production') {
    console.log('🔧 Production build detected - using real auth');
    return 'real';
  }

  // Check environment variable for auth mode
  if (process.env.REACT_APP_DISABLE_AUTH === 'true') {
    return 'mock';
  }

  // Default to real auth
  return 'real';
})();

const shouldUseMockAuth = (): boolean => {
  // Runtime override for development/testing
  if (typeof window !== 'undefined') {
    const devOverride = localStorage.getItem('DEV_FORCE_REAL_AUTH');
    if (devOverride === 'true') {
      return false;
    }

    const devMockOverride = localStorage.getItem('DEV_FORCE_MOCK_AUTH');
    if (devMockOverride === 'true') {
      return true;
    }
  }

  // Domain-based detection for server environments
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.includes('swarmind.ai')) {
      return false;
    }

    // Non-localhost domains use real auth
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return false;
    }
  }

  // Use the build-time determined mode
  return AUTH_MODE === 'mock';
};

export const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const useMock = shouldUseMockAuth();

  if (useMock) {
    return <MockAuthProvider>{children}</MockAuthProvider>;
  }

  return <AuthProvider>{children}</AuthProvider>;
};

// Create a safe auth hook that doesn't violate hooks rules
// The key is that the provider choice is made at the wrapper level
// So we know which context will be available
export const useAuth = () => {
  const useMock = shouldUseMockAuth();

  // This is safe because the provider choice is consistent
  // and determined by the same logic
  // eslint-disable-next-line react-hooks/rules-of-hooks
  if (useMock) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMockAuth();
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useRealAuth();
  }
};

// Export auth status for debugging
export const getAuthMode = () => {
  const useMock = shouldUseMockAuth();
  return {
    mode: useMock ? 'mock' : 'real',
    isMockAuth: useMock,
    isRealAuth: !useMock,
    isAuthDisabled: useMock,
    environment: process.env.NODE_ENV,
    disableAuthEnv: process.env.REACT_APP_DISABLE_AUTH,
  };
};
